import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, input, Output, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-task-rich-text-editor',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './task-rich-text-editor.component.html',
  styleUrl: './task-rich-text-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TaskRichTextEditorComponent),
      multi: true,
    },
  ],
})
export class TaskRichTextEditorComponent implements ControlValueAccessor, AfterViewInit {
  readonly placeholder = input('Describe the task');
  readonly editorRef = viewChild<ElementRef<HTMLDivElement>>('editorRef');
  readonly value = signal('');
  readonly disabled = signal(false);

  @Output() readonly valueChanged = new EventEmitter<string>();

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private pendingValue: string | null = null;

  ngAfterViewInit(): void {
    this.syncEditor(this.pendingValue ?? this.value());
    this.pendingValue = null;
  }

  writeValue(value: string | null): void {
    const next = value ?? '';
    this.value.set(next);
    this.pendingValue = next;
    this.syncEditor(next);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onInput(event: Event): void {
    const html = (event.target as HTMLDivElement | null)?.innerHTML ?? '';
    const normalized = this.normalizeHtml(html);
    this.value.set(normalized);
    this.onChange(normalized);
    this.valueChanged.emit(normalized);
  }

  onBlur(): void {
    this.onTouched();
  }

  applyCommand(command: 'bold' | 'italic' | 'insertUnorderedList' | 'insertOrderedList'): void {
    if (this.disabled()) {
      return;
    }
    this.focusEditor();
    document.execCommand(command);
    this.emitEditorValue();
  }

  addLink(): void {
    if (this.disabled()) {
      return;
    }
    const url = typeof window !== 'undefined'
      ? window.prompt('Enter link URL')
      : null;
    if (!url) {
      return;
    }
    this.focusEditor();
    document.execCommand('createLink', false, url.trim());
    this.emitEditorValue();
  }

  clearFormatting(): void {
    if (this.disabled()) {
      return;
    }
    this.focusEditor();
    document.execCommand('removeFormat');
    this.emitEditorValue();
  }

  private emitEditorValue(): void {
    const html = this.normalizeHtml(this.editorRef()?.nativeElement.innerHTML ?? '');
    this.value.set(html);
    this.onChange(html);
    this.valueChanged.emit(html);
  }

  private focusEditor(): void {
    this.editorRef()?.nativeElement.focus();
  }

  private syncEditor(value: string): void {
    const editor = this.editorRef()?.nativeElement;
    if (!editor) {
      return;
    }
    if (editor.innerHTML !== value) {
      editor.innerHTML = value;
    }
  }

  private normalizeHtml(value: string): string {
    return value
      .replace(/<div><br><\/div>/gi, '')
      .replace(/<div>/gi, '<p>')
      .replace(/<\/div>/gi, '</p>')
      .replace(/&nbsp;/gi, ' ')
      .trim();
  }
}
